import React, { useState, useEffect } from 'react';
import { sportsApi } from '../services/sportsApi';
import { Matchday, Season, Competition } from '../types/sports';
import MatchdayForm from '../components/matchdays/MatchdayForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Plus, Pencil, Trash2, RefreshCw, CheckCircle, LayoutGrid, List, BarChart3, Pause, Play } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Matchdays overview page with list and card view
 */
const Matchdays: React.FC = () => {
  const [matchdays, setMatchdays] = useState<Matchday[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMatchday, setEditingMatchday] = useState<Matchday | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [matchdayToDelete, setMatchdayToDelete] = useState<Matchday | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchdaysData, seasonsData, competitionsData] = await Promise.all([
        sportsApi.getAllMatchdays(),
        sportsApi.getAllSeasons(),
        sportsApi.getAllCompetitions()
      ]);
      setMatchdays(matchdaysData);
      setSeasons(seasonsData);
      setCompetitions(competitionsData);
    } catch (err) {
      setError('Fehler beim Laden der Spieltage');
      console.error('Error loading matchdays:', err);
    } finally {
      setLoading(false);
    }
  };

  // CRUD Functions
  const handleCreateMatchday = () => {
    setEditingMatchday(null);
    setShowForm(true);
  };

  const handleEditMatchday = (matchday: Matchday) => {
    setEditingMatchday(matchday);
    setShowForm(true);
  };

  const handleDeleteClick = async (matchday: Matchday) => {
    try {
      const result = await sportsApi.canDeleteMatchday(matchday.id);
      if (!result.canDelete) {
        showError('Dieser Spieltag kann nicht gelöscht werden, da er von Spielen referenziert wird.');
        return;
      }
      setMatchdayToDelete(matchday);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error('Error checking if matchday can be deleted:', error);
      showError('Fehler beim Prüfen der Löschberechtigung.');
    }
  };

  const handleSaveMatchday = async (data: {
    name: string;
    number: number;
    startDate?: string;
    endDate?: string;
    isCompleted?: boolean;
    isActive?: boolean;
    seasonId: number;
    competitionId?: number;
  }) => {
    try {
      setFormLoading(true);
      if (editingMatchday) {
        await sportsApi.updateMatchday(editingMatchday.id, data);
        showSuccess('Spieltag erfolgreich aktualisiert');
      } else {
        await sportsApi.createMatchday(data);
        showSuccess('Spieltag erfolgreich erstellt');
      }
      setShowForm(false);
      setEditingMatchday(null);
      await loadData();
    } catch (err: any) {
      const errorMessage = err.message || (editingMatchday ? 'Fehler beim Aktualisieren des Spieltags' : 'Fehler beim Erstellen des Spieltags');
      showError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (matchday: Matchday) => {
    try {
      await sportsApi.toggleMatchdayActive(matchday.id);
      showSuccess(`Spieltag ${matchday.isActive ? 'deaktiviert' : 'aktiviert'}`);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Ändern des Aktivitätsstatus');
    }
  };

  const handleToggleCompleted = async (matchday: Matchday) => {
    try {
      await sportsApi.toggleMatchdayCompleted(matchday.id);
      showSuccess(`Spieltag als ${matchday.isCompleted ? 'nicht abgeschlossen' : 'abgeschlossen'} markiert`);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Ändern des Abschlussstatus');
    }
  };

  const confirmDelete = async () => {
    if (!matchdayToDelete) return;

    try {
      await sportsApi.deleteMatchday(matchdayToDelete.id);
      showSuccess('Spieltag erfolgreich gelöscht');
      setShowDeleteConfirm(false);
      setMatchdayToDelete(null);
      await loadData();
    } catch (err: any) {
      showError(err.message || 'Fehler beim Löschen des Spieltags');
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const filteredMatchdays = matchdays.filter(matchday => {
    const matchesSearch = matchday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      matchday.number.toString().includes(searchTerm) ||
      (matchday.season?.name && matchday.season.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (matchday.season?.league?.name && matchday.season.league.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (matchday.competition?.name && matchday.competition.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Spieltage werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2>Fehler</h2>
          <p>{error}</p>
          <button onClick={loadData}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="page-badge page-badge-active" style={{ padding: '12px', marginBottom: '12px', borderRadius: '6px' }}>
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="page-badge page-badge-inactive" style={{ padding: '12px', marginBottom: '12px', borderRadius: '6px' }}>
          {errorMessage}
        </div>
      )}

      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleCreateMatchday}>
            <Plus size={14} /> Neu
          </button>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Spieltage durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredMatchdays.length} / {matchdays.length} Spieltage</span>
        </div>
      </div>

      {/* Content */}
      {filteredMatchdays.length === 0 ? (
        <div className="page-empty">
          <h3>Keine Spieltage gefunden</h3>
          <p>Versuchen Sie andere Filter oder Suchbegriffe.</p>
        </div>
      ) : viewMode === 'cards' ? (

        <div className="page-cards-grid">
          {filteredMatchdays.map((matchday) => (
            <div key={matchday.id} className="page-card">
              <div className="page-card-header">
                <h3 className="page-card-title">
                  {matchday.name}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {matchday.isCompleted && (
                    <span className="page-badge page-badge-info">Abgeschlossen</span>
                  )}
                  <span className={`page-badge ${matchday.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                    {matchday.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
              </div>
              <div className="page-card-content">
                <div className="page-card-row">
                  <strong>Spieltag:</strong> {matchday.number}
                </div>
                <div className="page-card-row">
                  <strong>Saison:</strong> {matchday.season?.name || `Saison ID: ${matchday.seasonId}`}
                </div>
                {matchday.season?.league && (
                  <div className="page-card-row">
                    <strong>Liga:</strong> {matchday.season.league.name}
                  </div>
                )}
                {matchday.season?.league?.sport && (
                  <div className="page-card-row">
                    <strong>Sport:</strong> {matchday.season.league.sport.name}
                  </div>
                )}
                {matchday.season?.league?.country && (
                  <div className="page-card-row">
                    <strong>Land:</strong>
                    {matchday.season.league.country.flag && (
                      <span style={{ marginRight: '0.5rem' }}>{matchday.season.league.country.flag}</span>
                    )}
                    {matchday.season.league.country.name}
                  </div>
                )}
                {matchday.competition && (
                  <div className="page-card-row">
                    <strong>Wettbewerb:</strong> {matchday.competition.name}
                  </div>
                )}
                {matchday.startDate && (
                  <div className="page-card-row">
                    <strong>Start:</strong> {new Date(matchday.startDate).toLocaleDateString('de-DE')}
                  </div>
                )}
                {matchday.endDate && (
                  <div className="page-card-row">
                    <strong>Ende:</strong> {new Date(matchday.endDate).toLocaleDateString('de-DE')}
                  </div>
                )}
              </div>
              <div className="page-card-actions">
                <span className="page-card-footer">
                  Erstellt: {new Date(matchday.createdAt).toLocaleDateString('de-DE')}
                </span>
                <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                  <button
                    className={`page-btn-icon ${matchday.isActive ? 'toggle' : 'edit'}`}
                    onClick={() => handleToggleActive(matchday)}
                    title={matchday.isActive ? "Deaktivieren" : "Aktivieren"}
                  >
                    {matchday.isActive ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <button
                    className={`page-btn-icon ${matchday.isCompleted ? 'toggle' : 'edit'}`}
                    onClick={() => handleToggleCompleted(matchday)}
                    title={matchday.isCompleted ? "Öffnen" : "Abschließen"}
                  >
                    {matchday.isCompleted ? <RefreshCw size={14} /> : <CheckCircle size={14} />}
                  </button>
                  <button className="page-btn-icon edit" onClick={() => handleEditMatchday(matchday)} title="Bearbeiten">
                    <Pencil size={14} />
                  </button>
                  <button className="page-btn-icon delete" onClick={() => handleDeleteClick(matchday)} title="Löschen">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (

        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Spieltag</th>
                <th>Saison / Liga</th>
                <th>Wettbewerb</th>
                <th>Datum</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatchdays.map((matchday) => (
                <tr key={matchday.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>{matchday.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Spieltag {matchday.number}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>{matchday.season?.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{matchday.season?.league?.name}</div>
                    </div>
                  </td>
                  <td>{matchday.competition?.name || '-'}</td>
                  <td>{matchday.startDate ? new Date(matchday.startDate).toLocaleDateString('de-DE') : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className={`page-badge ${matchday.isActive ? 'page-badge-active' : 'page-badge-inactive'}`}>
                        {matchday.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {matchday.isCompleted && (
                        <span className="page-badge page-badge-info">Abgeschlossen</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="page-table-actions">
                      <button
                        className={`page-btn-icon ${matchday.isActive ? 'toggle' : 'edit'}`}
                        onClick={() => handleToggleActive(matchday)}
                        title={matchday.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {matchday.isActive ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button
                        className={`page-btn-icon ${matchday.isCompleted ? 'toggle' : 'edit'}`}
                        onClick={() => handleToggleCompleted(matchday)}
                        title={matchday.isCompleted ? 'Öffnen' : 'Abschließen'}
                      >
                        {matchday.isCompleted ? <RefreshCw size={14} /> : <CheckCircle size={14} />}
                      </button>
                      <button className="page-btn-icon edit" onClick={() => handleEditMatchday(matchday)} title="Bearbeiten">
                        <Pencil size={14} />
                      </button>
                      <button className="page-btn-icon delete" onClick={() => handleDeleteClick(matchday)} title="Löschen">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm && !!matchdayToDelete}
        title="Spieltag löschen"
        message={`Möchten Sie den Spieltag "${matchdayToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setMatchdayToDelete(null);
        }}
        type="danger"
      />

      {/* Form Modal */}
      {showForm && (
        <MatchdayForm
          matchday={editingMatchday || undefined}
          onCancel={() => {
            setShowForm(false);
            setEditingMatchday(null);
          }}
          onSave={handleSaveMatchday}
          seasons={seasons}
          competitions={competitions}
          loading={formLoading}
        />
      )}
    </div>
  );
};

export default Matchdays;
